/**
 * burst-guard cost counters for You › Machines: each machine's encrypted-report delivery
 * (vikod's sealed `reportStats`), this tab's own report reads and hub requests, and the account's
 * daily hub budget when the hub serves it. Each is a labeled compact chip; a click opens the
 * full numbers (never truncated).
 */
import { useQuery } from '@tanstack/react-query'
import type { ReportStats } from '@viko/protocol'
import { useEffect, useId, useState, useSyncExternalStore } from 'react'
import { useBackend } from '../lib/backend.js'
import { activePoll } from '../lib/query-client.js'
import { type MeterKind, type RequestMeter, requestMeter } from '../lib/request-meter.js'
import { Ago } from './ui.js'

const n = (v: number) => v.toLocaleString()

function Chip({
  label,
  value,
  tone,
  children,
}: {
  label: string
  value: string
  tone?: 'warn' | 'fail'
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <div className="grid gap-2">
      <button
        type="button"
        className="vk-chip vk-chip--button justify-self-start text-[14px]"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="vk-chip__text">
          <span className="text-text-3">{label}</span>{' '}
          <span
            className={`font-semibold ${tone === 'fail' ? 'text-status-fail' : tone === 'warn' ? 'text-status-wait' : 'text-text-1'}`}
          >
            {value}
          </span>
        </span>
      </button>
      {open ? (
        <dl
          id={id}
          className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-surface-2 px-3 py-2 text-[14px]"
        >
          {children}
        </dl>
      ) : null}
    </div>
  )
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-3">{k}</dt>
      <dd className="m-0 text-text-2 tabular-nums">{v}</dd>
    </>
  )
}

/** One machine's report delivery counters (from its newest sealed status report). */
export function MachineReportStats({ stats, name }: { stats: ReportStats; name: string }) {
  const now = useNow()
  const deferred = stats.deferredUntil && stats.deferredUntil > now ? stats.deferredUntil : 0
  return (
    <Chip
      label="Report puts"
      value={`${n(stats.putsLastMinute)}/min`}
      {...(deferred ? { tone: 'warn' as const } : {})}
    >
      <Row k="Machine" v={name} />
      <Row k="Puts, last minute" v={n(stats.putsLastMinute)} />
      <Row k="Puts, last hour" v={n(stats.putsLastHour)} />
      <Row k="Peak per minute (hour)" v={n(stats.peakPerMinute)} />
      <Row k="Coalesced, last hour" v={n(stats.coalescedLastHour)} />
      <Row k="Queued on machine" v={n(stats.queued)} />
      <Row
        k="Deferred until"
        v={
          deferred ? (
            <>
              {new Date(deferred).toLocaleTimeString()} (rate limited, in{' '}
              {n(Math.ceil((deferred - now) / 1000))} s)
            </>
          ) : (
            'not deferred'
          )
        }
      />
      <Row k="Counted" v={<Ago ts={stats.at} />} />
    </Chip>
  )
}

function useNow(everyMs = 15_000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(t)
  }, [everyMs])
  return now
}

function useMeter(meter: RequestMeter) {
  useSyncExternalStore(meter.subscribe, meter.version)
  useNow() // trailing-minute counts decay without new requests
  return (k: MeterKind) => meter.stats(k)
}

/** This tab's own report reads and hub requests (trailing minute, hour and peak). */
export function TabRequestMeter({ meter = requestMeter }: { meter?: RequestMeter }) {
  const stats = useMeter(meter)
  const list = stats('report.list'),
    read = stats('report.read'),
    http = stats('http')
  return (
    <Chip label="Report reads (this tab)" value={`${n(list.lastMinute + read.lastMinute)}/min`}>
      <Row k="Report lists, last minute" v={n(list.lastMinute)} />
      <Row k="Report lists, last hour" v={n(list.lastHour)} />
      <Row k="Report lists, peak per minute" v={n(list.peakPerMinute)} />
      <Row k="Report reads, last minute" v={n(read.lastMinute)} />
      <Row k="Report reads, last hour" v={n(read.lastHour)} />
      <Row k="Report reads, peak per minute" v={n(read.peakPerMinute)} />
      <Row k="All hub requests, last minute" v={n(http.lastMinute)} />
      <Row k="All hub requests, last hour" v={n(http.lastHour)} />
      <Row k="All hub requests, peak per minute" v={n(http.peakPerMinute)} />
    </Chip>
  )
}

/** The account's daily hub request budget; renders nothing where the hub has no budget route. */
export function HubBudget() {
  const b = useBackend()
  const q = useQuery({
    queryKey: ['budget'],
    queryFn: () => b.api.budget?.() ?? Promise.resolve(null),
    retry: false,
    ...activePoll(5 * 60_000),
  })
  const d = q.data
  if (!d) return null
  const pct = d.budget ? Math.round((100 * d.used) / d.budget) : 0
  return (
    <Chip
      label="Hub requests today"
      value={`${n(d.used)} of ${n(d.budget)}`}
      {...(d.degraded ? { tone: 'fail' as const } : pct >= 80 ? { tone: 'warn' as const } : {})}
    >
      <Row k="Day (UTC)" v={d.day} />
      <Row k="Used" v={`${n(d.used)} (${n(pct)}%)`} />
      <Row k="Daily budget" v={n(d.budget)} />
      <Row
        k="Mode"
        v={d.degraded ? 'Degraded: background refreshes paused until the UTC reset' : 'Normal'}
      />
      <Row
        k="Alerts crossed"
        v={d.alerts.length ? d.alerts.map((a) => `${n(a)}%`).join(', ') : 'none'}
      />
    </Chip>
  )
}
